/**
 * useFeatureDefinitions — Hook for querying available features (subgraphs).
 *
 * Features map 1:1 to subgraphs. Each subgraph config defines feature metadata
 * (category, dependencies, tags) for the setup wizard. Products are optional
 * bundles that pre-select features + add regulatory overlays.
 *
 * Data source: availableFeatures query reads common/configs/seed/subgraphs configs
 * Dev fallback: Mock features from subgraph configs when backend unavailable.
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

export const LIST_AVAILABLE_FEATURES = gql`
  query ListAvailableFeatures {
    availableFeatures {
      featureId
      name
      description
      icon
      color
      category
      isCore
      isLifeCritical
      complianceFrameworks
      dependencies
      tags
      subgraphId
      shortDescription
    }
  }
`;

export interface FeatureDefinition {
  featureId: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  category: string;
  isCore: boolean;
  isLifeCritical: boolean;
  complianceFrameworks: string[];
  dependencies: string[];
  tags: string[];
  subgraphId: string;
  shortDescription: string;
}

export interface UseFeatureDefinitionsReturn {
  features: FeatureDefinition[];
  loading: boolean;
  error: Error | undefined;
}

// Dev-mode env detection (same pattern as useTenantSubgraphConfig)
const getEnvConfig = (): Record<string, string> => {
  if (typeof window !== 'undefined' && (window as unknown as { __ENV__?: Record<string, string> }).__ENV__) {
    return (window as unknown as { __ENV__: Record<string, string> }).__ENV__;
  }
  return {};
};
const USE_MOCK_AUTH = getEnvConfig().ENABLE_MOCK_AUTH === 'true';
const USE_DEV_MODE = getEnvConfig().ENABLE_DEV_MODE === 'true';

/**
 * Mock feature definitions derived from common/configs/seed/subgraphs configs.
 * Used as dev-mode fallback when backend is unavailable.
 */
const MOCK_FEATURES: FeatureDefinition[] = [
  {
    featureId: 'ferp-core',
    name: 'FERP Core',
    description: '25 primitives, workflow engine, audit trails, and approval pipelines',
    shortDescription: '25 primitives, workflow engine, audit trails',
    icon: 'IconStack',
    color: 'gray',
    category: 'infrastructure',
    isCore: true,
    isLifeCritical: false,
    complianceFrameworks: ['SOC2'],
    dependencies: [],
    tags: ['cross-industry', 'always-on'],
    subgraphId: 'ferp-core',
  },
  {
    featureId: 'erp',
    name: 'Accounting & ERP',
    description: 'General ledger, accounts payable/receivable, billing, invoicing, and financial reporting',
    shortDescription: 'GL, AP/AR, billing, invoicing',
    icon: 'IconCalculator',
    color: 'blue',
    category: 'operations',
    isCore: false,
    isLifeCritical: false,
    complianceFrameworks: ['SOX', 'GAAP'],
    dependencies: ['ferp-core'],
    tags: ['cross-industry', 'financial'],
    subgraphId: 'erp',
  },
  {
    featureId: 'payroll',
    name: 'Payroll & Workforce',
    description: 'Payroll processing, time tracking, shift scheduling, benefits management, tax compliance, and leave administration',
    shortDescription: 'Payroll, time tracking, scheduling, benefits',
    icon: 'IconCash',
    color: 'green',
    category: 'operations',
    isCore: false,
    isLifeCritical: false,
    complianceFrameworks: ['FLSA', 'IRS'],
    dependencies: ['ferp-core'],
    tags: ['cross-industry', 'financial'],
    subgraphId: 'payroll',
  },
  {
    featureId: 'fhir',
    name: 'Healthcare (FHIR)',
    description: 'Clinical data, patient records, care plans, and FHIR R4 interoperability',
    shortDescription: 'Clinical data, patient records, FHIR R4',
    icon: 'IconHeartbeat',
    color: 'red',
    category: 'healthcare',
    isCore: false,
    isLifeCritical: true,
    complianceFrameworks: ['HIPAA', 'HL7_FHIR', 'CMS'],
    dependencies: ['ferp-core'],
    tags: ['healthcare', 'clinical'],
    subgraphId: 'fhir',
  },
  {
    featureId: 'edi',
    name: 'EDI & Claims',
    description: 'Electronic data interchange, claims submission (837/835), eligibility (270/271)',
    shortDescription: 'EDI transactions, claims processing',
    icon: 'IconFileExport',
    color: 'violet',
    category: 'healthcare',
    isCore: false,
    isLifeCritical: false,
    complianceFrameworks: ['HIPAA', 'X12'],
    dependencies: ['ferp-core', 'erp'],
    tags: ['healthcare', 'financial'],
    subgraphId: 'edi',
  },
  {
    featureId: 'pdf-studio',
    name: 'PDF Studio',
    description: 'Advanced PDF intelligence: ML-powered classification, OCR extraction, signing ceremonies, and KMS-secured document workflows',
    shortDescription: 'PDF intelligence, signing, OCR',
    icon: 'IconFileTypePdf',
    color: 'pink',
    category: 'infrastructure',
    isCore: false,
    isLifeCritical: false,
    complianceFrameworks: ['SOC2', 'HIPAA'],
    dependencies: ['ferp-core'],
    tags: ['cross-industry', 'document'],
    subgraphId: 'pdf-studio',
  },
  {
    featureId: 'wearables',
    name: 'Wearables',
    description: 'Hands-free workflow execution via Vuzix smart glasses for field operations and care delivery',
    shortDescription: 'Smart glasses, hands-free workflows',
    icon: 'IconDeviceVisionPro',
    color: 'indigo',
    category: 'healthcare',
    isCore: false,
    isLifeCritical: true,
    complianceFrameworks: ['HIPAA'],
    dependencies: ['ferp-core', 'fhir'],
    tags: ['healthcare', 'field-ops'],
    subgraphId: 'wearables',
  },
  {
    featureId: 'website',
    name: 'Website Builder',
    description: 'Business website with pages, components, and deployment management',
    shortDescription: 'Website pages, components, deploy',
    icon: 'IconWorld',
    color: 'orange',
    category: 'infrastructure',
    isCore: false,
    isLifeCritical: false,
    complianceFrameworks: [],
    dependencies: ['ferp-core'],
    tags: ['cross-industry', 'marketing'],
    subgraphId: 'website',
  },
];

export function useFeatureDefinitions(): UseFeatureDefinitionsReturn {
  // Skip the query entirely in dev/mock mode (same pattern as useTenantSubgraphConfig)
  const skipQuery = USE_MOCK_AUTH || USE_DEV_MODE;

  const { data, loading, error } = useQuery<{
    availableFeatures: FeatureDefinition[];
  }>(LIST_AVAILABLE_FEATURES, {
    fetchPolicy: 'cache-and-network',
    skip: skipQuery,
  });

  const backendData = data?.availableFeatures;
  const backendFailed = error != null || (!loading && !backendData);

  const features = useMemo(() => {
    // Prefer backend data when available
    if (backendData && backendData.length > 0) {
      return backendData;
    }

    // Dev mode fallback: use mock features when backend unavailable
    if ((USE_DEV_MODE || USE_MOCK_AUTH) && backendFailed) {
      console.warn('⚠️  Using MOCK feature definitions (backend unavailable, dev mode fallback)');
      return MOCK_FEATURES;
    }

    return [];
  }, [backendData, backendFailed]);

  return {
    features,
    loading: loading && !skipQuery,
    error: backendFailed && !skipQuery ? error : undefined,
  };
}
